import { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Reveal, Overline } from "@/components/Reveal";

const DATA = [
  { year: "FY26", revenue: 120.0 },
  { year: "FY27", revenue: 162.56 },
  { year: "FY28", revenue: 210.68 },
  { year: "FY29", revenue: 264.64 },
  { year: "FY30", revenue: 324.73 },
  { year: "FY31", revenue: 391.26 },
  { year: "FY32", revenue: 464.54 },
];

const fmtCr = (lacs) => `₹${(lacs / 100).toFixed(2)} Cr`;

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-lime/30 bg-navy-deep px-4 py-3 shadow-xl">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-lime">{label}</p>
      <p className="mt-1 font-display text-lg font-bold text-white">{fmtCr(payload[0].value)}</p>
      <p className="text-xs text-slate-400">projected turnover</p>
    </div>
  );
};

export default function GrowthChart() {
  const [hovered, setHovered] = useState(DATA[6]);

  return (
    <section className="mx-auto max-w-6xl px-6 pt-28" data-testid="growth-chart">
      <Reveal>
        <Overline>The Trajectory</Overline>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-xl">
            Ride a brand growing <span className="text-sun-bright">4x in seven years.</span>
          </h2>
          <div className="text-right">
            <p className="font-display text-3xl sm:text-4xl font-extrabold text-lime" data-testid="growth-chart-figure">
              {fmtCr(hovered.revenue)}
            </p>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              projected turnover · {hovered.year}
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-10 rounded-[2rem] border border-white/10 bg-navy-card p-6 sm:p-10">
          <div className="h-[300px] sm:h-[360px] w-full" data-testid="growth-chart-canvas">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={DATA}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                onMouseMove={(s) => {
                  if (s?.activePayload?.length) setHovered(s.activePayload[0].payload);
                }}
                onMouseLeave={() => setHovered(DATA[6])}
              >
                <defs>
                  <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#84CC16" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#84CC16" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis
                  dataKey="year"
                  stroke="transparent"
                  tick={{ fill: "#94A3B8", fontSize: 12, fontWeight: 600 }}
                  tickLine={false}
                  axisLine={false}
                  dy={10}
                  padding={{ left: 24, right: 24 }}
                />
                <YAxis hide domain={[0, 500]} />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: "rgba(132,204,22,0.4)", strokeWidth: 1.5, strokeDasharray: "5 5" }} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#84CC16"
                  strokeWidth={3}
                  fill="url(#revFill)"
                  animationDuration={1800}
                  animationEasing="ease-out"
                  activeDot={{ r: 6, fill: "#FACC15", stroke: "#0A1128", strokeWidth: 3 }}
                  dot={{ r: 4, fill: "#0A1128", stroke: "#84CC16", strokeWidth: 2.5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-10 gap-y-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            <span><span className="text-sun-bright">+35%</span> growth in year two</span>
            <span><span className="text-sun-bright">₹4.65 Cr</span> projected by FY32</span>
            <span><span className="text-sun-bright">16%</span> project IRR</span>
            <span>Source: Ananya Ventures project report</span>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
